import React, { useMemo, useReducer } from 'react';
import {
  FreeProductsModalProps,
  GlobalAppContextMenuInterface,
  LoginModalProps,
  MenuItemModalProps,
  OrderBasketModalProps,
  OrderMethodModalProps,
  OtpVerificationModalProps,
  ReducerDispatchActionPayloadInterface,
  RestaurantClosedModalProps,
} from '../services/exports/Interfaces';
import { APP_STATE_REDUCER_TYPES } from '../services/exports/Constants';
import { ConfirmCancelModalProps } from '../modals/ConfirmCancelModal';

interface Props {
  children: any;
}

export interface GlobalAppStateContextInterface {
  loginModalProps: LoginModalProps;
  infoModalProps: ConfirmCancelModalProps;
  menu: Partial<GlobalAppContextMenuInterface>;
  menuItemModalProps: MenuItemModalProps;
  orderMethodModalProps: OrderMethodModalProps;
  otpVerificationModalProps: OtpVerificationModalProps;
  freeProductsModalProps: FreeProductsModalProps;
  restaurantClosedModalProps: RestaurantClosedModalProps;
  orderBasketModalProps: OrderBasketModalProps;
}

export interface GlobalAppContextReducerHookInterface {
  state: GlobalAppStateContextInterface;
  dispatch: (
    data: ReducerDispatchActionPayloadInterface<APP_STATE_REDUCER_TYPES, Partial<GlobalAppStateContextInterface>>,
  ) => void;
}

export const AppStateContext: React.Context<GlobalAppContextReducerHookInterface> = React.createContext(null);

export default function AppStateProvider(props: Props): JSX.Element {
  const { children } = props;

  const initialState: GlobalAppStateContextInterface = {
    loginModalProps: {
      openModal: false,
      isStatic: false,
      initialPhoneNumber: undefined,
    },
    infoModalProps: {
      openModal: false,
    },
    freeProductsModalProps: {
      openModal: false,
    },
    menuItemModalProps: {
      openModal: false,
      product: null,
    },
    menu: {
      screenPosition: 0,
      activeCategoryId: null,
      menuLoaded: false,
    },
    orderMethodModalProps: {
      openModal: false,
    },
    otpVerificationModalProps: {
      openModal: false,
    },
    restaurantClosedModalProps: {
      openModal: false,
    },
    orderBasketModalProps: {
      openModal: false,
    },
  };

  const [state, dispatch] = useReducer<
    (
      prevState: GlobalAppStateContextInterface,
      action: ReducerDispatchActionPayloadInterface<APP_STATE_REDUCER_TYPES, Partial<GlobalAppStateContextInterface>>,
    ) => any
  >(
    (
      prevState: GlobalAppStateContextInterface,
      action: ReducerDispatchActionPayloadInterface<APP_STATE_REDUCER_TYPES, Partial<GlobalAppStateContextInterface>>,
    ) => {
      const { payload } = action;
      switch (action.type) {
        case APP_STATE_REDUCER_TYPES.TOGGLE_LOGIN_MODAL:
          return {
            ...prevState,
            loginModalProps: payload?.loginModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_INFO_MODAL:
          return {
            ...prevState,
            infoModalProps: payload?.infoModalProps,
          };
        case APP_STATE_REDUCER_TYPES.SAVE_MENU:
          return {
            ...prevState,
            menu: {
              ...prevState.menu,
              ...payload.menu,
            },
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_MENU_ITEM_MODAL:
          return {
            ...prevState,
            menuItemModalProps: payload?.menuItemModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_ORDER_METHOD_MODAL:
          return {
            ...prevState,
            orderMethodModalProps: payload?.orderMethodModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_OTP_VERIFICATION_MODAL:
          return {
            ...prevState,
            otpVerificationModalProps: payload?.otpVerificationModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_FREE_PRODUCTS_MODAL:
          return {
            ...prevState,
            freeProductsModalProps: payload?.freeProductsModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_RESTAURANT_CLOSED_MODAL:
          return {
            ...prevState,
            restaurantClosedModalProps: payload?.restaurantClosedModalProps,
          };
        case APP_STATE_REDUCER_TYPES.TOGGLE_ORDER_BASKET_MODAL:
          return {
            ...prevState,
            orderBasketModalProps: payload?.orderBasketModalProps,
          };
      }
    },
    initialState,
  );

  const context = useMemo(
    () => ({
      state,
      dispatch,
    }),
    [state, dispatch],
  );

  return <AppStateContext.Provider value={context}>{children}</AppStateContext.Provider>;
}
